import React, { useState, useRef, useEffect } from 'react';
import {
  Building2,
  Ship,
  Globe,
  Paintbrush,
  ExternalLink,
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Settings,
  ShieldCheck,
  Clock,
  Sparkles,
  Layers,
  Copy,
  Link,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Download,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { IndustryType, SiteData } from '../../types';
import { SiteDuplicateModal } from './SiteDuplicateModal';
import { SectorSelectionModal } from '../onboarding/SectorSelectionModal';
import { WebsiteExportModal } from '../export/WebsiteExportModal';
import { getSiteDisplayAddress } from '../../utils/security';

interface SitesListProps {
  onOpenWizard: (industry?: IndustryType) => void;
  onOpenAiWizard?: (industry?: IndustryType) => void;
  onOpenEditor: (siteId: string) => void;
  onOpenLiveSite: (site: SiteData) => void;
  onSelectSite: (siteId: string) => void;
  onNavigate?: (view: string) => void;
}

export const SitesList: React.FC<SitesListProps> = ({
  onOpenWizard,
  onOpenAiWizard,
  onOpenEditor,
  onOpenLiveSite,
  onSelectSite,
  onNavigate,
}) => {
  const { logout } = useAuth();
  const {
    sites,
    activeSite,
    setActiveSiteById,
    deleteSite,
    sectorQuotas,
    canCreateSectorWebsite,
    totalRemainingQuota,
    websiteLimit,
    remainingWebsiteQuota,
    canCreateWebsite,
  } = useSite();
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState<'ALL' | IndustryType>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PUBLISHED' | 'DRAFT'>('ALL');
  const [siteToDelete, setSiteToDelete] = useState<SiteData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccessAlert, setDeleteSuccessAlert] = useState(false);
  const [deleteErrorAlert, setDeleteErrorAlert] = useState(false);
  const [activeSiteExistsModalOpen, setActiveSiteExistsModalOpen] = useState(false);
  const [openDropdownSiteId, setOpenDropdownSiteId] = useState<string | null>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [quotaExceededModalOpen, setQuotaExceededModalOpen] = useState(false);
  const [isSectorSelectionOpen, setIsSectorSelectionOpen] = useState(false);
  const [exportModalSite, setExportModalSite] = useState<SiteData | null>(null);

  const activeNonDeletedSites = sites.filter((site) => !site.isDeleted && site.status !== 'DELETED');
  const hasActiveWebsite = activeNonDeletedSites.length >= 1;
  const hasWebsiteSubscription = sectorQuotas.CORPORATE.allowed || sectorQuotas.YACHT_RENTAL.allowed;

  const handleCreateAttempt = (action: 'ai' | 'standard') => {
    // 1. Check if user has an active website subscription
    if (!hasWebsiteSubscription) {
      if (onNavigate) {
        onNavigate('billing');
      } else {
        setQuotaExceededModalOpen(true);
      }
      return;
    }

    // 2. Requirement 14: If user already has an active website, block and show alert
    if (hasActiveWebsite) {
      setActiveSiteExistsModalOpen(true);
      return;
    }

    // 3. User has quota and no active site: Lock to active sector
    if (sectorQuotas.CORPORATE.allowed && !sectorQuotas.YACHT_RENTAL.allowed) {
      if (action === 'ai' && onOpenAiWizard) {
        onOpenAiWizard('CORPORATE');
      } else {
        onOpenWizard('CORPORATE');
      }
      return;
    }
    if (sectorQuotas.YACHT_RENTAL.allowed && !sectorQuotas.CORPORATE.allowed) {
      if (action === 'ai' && onOpenAiWizard) {
        onOpenAiWizard('YACHT_RENTAL');
      } else {
        onOpenWizard('YACHT_RENTAL');
      }
      return;
    }

    setIsSectorSelectionOpen(true);
  };

  const handleCreateNewAccount = async () => {
    setActiveSiteExistsModalOpen(false);
    try {
      await logout();
    } catch (err) {
      console.warn('Logout notice:', err);
    }
    window.location.href = '/kayit';
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.site-card-dropdown')) {
        setOpenDropdownSiteId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredSites = sites.filter((site) => {
    if (site.isDeleted || site.status === 'DELETED') return false;

    const term = (searchTerm || '').toLowerCase();
    const matchesSearch =
      (site.name || '').toLowerCase().includes(term) ||
      (site.subdomain ? site.subdomain.toLowerCase().includes(term) : false) ||
      (site.slug ? site.slug.toLowerCase().includes(term) : false) ||
      (site.customDomain ? site.customDomain.toLowerCase().includes(term) : false);

    const matchesIndustry = industryFilter === 'ALL' || site.industry === industryFilter;
    const matchesStatus = statusFilter === 'ALL' || site.status === statusFilter;

    return matchesSearch && matchesIndustry && matchesStatus;
  });

  const handleDeleteConfirm = async () => {
    if (!siteToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteSite(siteToDelete.id);
      setSiteToDelete(null);
      setDeleteSuccessAlert(true);
    } catch (err) {
      console.error('Site silinirken hata oluştu:', err);
      setDeleteErrorAlert(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatLastUpdate = (timestamp?: number) => {
    if (!timestamp) return 'Bugün güncellendi';
    try {
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return 'Yakın zamanda güncellendi';
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in select-none">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-600/10 text-blue-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Web Sitelerim
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Yönettiğiniz tüm aktif ve taslak web siteleri ({sites.length} Proje)
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          {onOpenAiWizard && (
            <button
              id="btn-ai-generate-site-sites-page"
              onClick={() => handleCreateAttempt('ai')}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white text-xs sm:text-sm font-bold shadow-md shadow-indigo-600/25 transition flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span>AI ile Site Oluştur</span>
            </button>
          )}

          <button
            id="btn-create-new-site-sites-page"
            onClick={() => handleCreateAttempt('standard')}
            className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm font-semibold shadow-md transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Site Ekle</span>
          </button>
        </div>
      </div>

      {/* Sektör Bazlı Website Kotası & Paket Durumu Banner'ı */}
      <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900">
                Sektör Bazlı Website Kotaları & Paket Hakları
              </span>
              <span className="text-[11px] font-semibold text-slate-400">
                (Toplam {sites.length} Aktif Site • {totalRemainingQuota} Kullanılabilir Hak)
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Her sektör bağımsız kotaya sahiptir. Kurumsal ve Yat Kiralama hakları birbirinden ayrı yönetilir.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsSectorSelectionOpen(true)}
            className="px-3.5 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold transition flex items-center gap-1.5 self-start sm:self-auto"
          >
            <span>Kota ve Sektör Detayları</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* 2 Sektör Kartı (Kurumsal & Yat Kiralama) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Kurumsal Sektör Kartı */}
          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 flex flex-col justify-between gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs border border-emerald-200 shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">Kurumsal & Şirket</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        sectorQuotas.CORPORATE.status === 'EXPIRED'
                          ? 'bg-rose-100 text-rose-800 border border-rose-300'
                          : sectorQuotas.CORPORATE.remaining > 0
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                          : sectorQuotas.CORPORATE.allowed
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-slate-200 text-slate-700 border border-slate-300'
                      }`}
                    >
                      {sectorQuotas.CORPORATE.status === 'EXPIRED'
                        ? 'Abonelik Süresi Doldu'
                        : sectorQuotas.CORPORATE.allowed
                        ? sectorQuotas.CORPORATE.remaining > 0
                          ? `${sectorQuotas.CORPORATE.remaining} Hak Mevcut`
                          : 'Hak Doldu (Kota 0)'
                        : 'Paketsiz'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600 mt-1">
                    <span>Toplam Hak: <strong className="text-slate-800">{sectorQuotas.CORPORATE.total}</strong></span>
                    <span>•</span>
                    <span>Kullanılan: <strong className="text-amber-700">{sectorQuotas.CORPORATE.used}</strong></span>
                    <span>•</span>
                    <span>Kalan: <strong className={sectorQuotas.CORPORATE.remaining > 0 ? 'text-emerald-700' : 'text-slate-600'}>{sectorQuotas.CORPORATE.remaining}</strong></span>
                  </div>
                </div>
              </div>

              {/* Action / Upsell */}
              {sectorQuotas.CORPORATE.remaining > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenAiWizard) {
                      onOpenAiWizard('CORPORATE');
                    } else {
                      onOpenWizard('CORPORATE');
                    }
                  }}
                  className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition shadow-xs flex items-center gap-1 shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Kurumsal Site Aç</span>
                </button>
              ) : (
                onNavigate && (
                  <button
                    type="button"
                    onClick={() => onNavigate('billing')}
                    className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition shadow-xs flex items-center gap-1 shrink-0"
                  >
                    <span>{sectorQuotas.CORPORATE.status === 'EXPIRED' ? 'Yenile' : sectorQuotas.CORPORATE.allowed ? 'Yükselt' : 'Paket Al'}</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )
              )}
            </div>

            {/* Quota Progress Bar */}
            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  sectorQuotas.CORPORATE.remaining > 0 ? 'bg-emerald-600' : 'bg-amber-500'
                }`}
                style={{
                  width: `${
                    sectorQuotas.CORPORATE.total > 0
                      ? Math.min(100, Math.round((sectorQuotas.CORPORATE.used / sectorQuotas.CORPORATE.total) * 100))
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Yat Kiralama Sektör Kartı */}
          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 flex flex-col justify-between gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-xs border border-cyan-200 shrink-0">
                  <Ship className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">Yat & Tekne Kiralama</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        sectorQuotas.YACHT_RENTAL.status === 'EXPIRED'
                          ? 'bg-rose-100 text-rose-800 border border-rose-300'
                          : sectorQuotas.YACHT_RENTAL.remaining > 0
                          ? 'bg-cyan-100 text-cyan-800 border border-cyan-300'
                          : sectorQuotas.YACHT_RENTAL.allowed
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-slate-200 text-slate-700 border border-slate-300'
                      }`}
                    >
                      {sectorQuotas.YACHT_RENTAL.status === 'EXPIRED'
                        ? 'Abonelik Süresi Doldu'
                        : sectorQuotas.YACHT_RENTAL.allowed
                        ? sectorQuotas.YACHT_RENTAL.remaining > 0
                          ? `${sectorQuotas.YACHT_RENTAL.remaining} Hak Mevcut`
                          : 'Hak Doldu (Kota 0)'
                        : 'Paketsiz'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600 mt-1">
                    <span>Toplam Hak: <strong className="text-slate-800">{sectorQuotas.YACHT_RENTAL.total}</strong></span>
                    <span>•</span>
                    <span>Kullanılan: <strong className="text-amber-700">{sectorQuotas.YACHT_RENTAL.used}</strong></span>
                    <span>•</span>
                    <span>Kalan: <strong className={sectorQuotas.YACHT_RENTAL.remaining > 0 ? 'text-cyan-700' : 'text-slate-600'}>{sectorQuotas.YACHT_RENTAL.remaining}</strong></span>
                  </div>
                </div>
              </div>

              {/* Action / Upsell */}
              {sectorQuotas.YACHT_RENTAL.remaining > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenAiWizard) {
                      onOpenAiWizard('YACHT_RENTAL');
                    } else {
                      onOpenWizard('YACHT_RENTAL');
                    }
                  }}
                  className="px-3 py-1.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold transition shadow-xs flex items-center gap-1 shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Yat Sitesi Aç</span>
                </button>
              ) : (
                onNavigate && (
                  <button
                    type="button"
                    onClick={() => onNavigate('billing')}
                    className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition shadow-xs flex items-center gap-1 shrink-0"
                  >
                    <span>{sectorQuotas.YACHT_RENTAL.status === 'EXPIRED' ? 'Yenile' : sectorQuotas.YACHT_RENTAL.allowed ? 'Yükselt' : 'Paket Al'}</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )
              )}
            </div>

            {/* Quota Progress Bar */}
            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  sectorQuotas.YACHT_RENTAL.remaining > 0 ? 'bg-cyan-600' : 'bg-amber-500'
                }`}
                style={{
                  width: `${
                    sectorQuotas.YACHT_RENTAL.total > 0
                      ? Math.min(100, Math.round((sectorQuotas.YACHT_RENTAL.used / sectorQuotas.YACHT_RENTAL.total) * 100))
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="p-3.5 sm:p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Site adı veya domain ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Industry Filter */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600">
            <button
              onClick={() => setIndustryFilter('ALL')}
              className={`px-3 py-1.5 rounded-lg transition ${
                industryFilter === 'ALL'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'hover:text-slate-900'
              }`}
            >
              Tümü
            </button>
            <button
              onClick={() => setIndustryFilter('CORPORATE')}
              className={`px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 ${
                industryFilter === 'CORPORATE'
                  ? 'bg-white text-emerald-700 shadow-xs'
                  : 'hover:text-emerald-700'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Kurumsal
            </button>
            <button
              onClick={() => setIndustryFilter('YACHT_RENTAL')}
              className={`px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 ${
                industryFilter === 'YACHT_RENTAL'
                  ? 'bg-white text-cyan-700 shadow-xs'
                  : 'hover:text-cyan-700'
              }`}
            >
              <Ship className="w-3.5 h-3.5" />
              Yat Kiralama
            </button>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Tüm Durumlar</option>
            <option value="PUBLISHED">Yayında Olanlar</option>
            <option value="DRAFT">Taslak Olanlar</option>
          </select>
        </div>
      </div>

      {/* Sites Grid */}
      {filteredSites.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center max-w-lg mx-auto space-y-4 shadow-xs">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
            <Globe className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Web Sitesi Bulunamadı</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            {searchTerm || industryFilter !== 'ALL' || statusFilter !== 'ALL'
              ? 'Arama kriterlerinize uygun site bulunamadı. Filtreleri temizlemeyi deneyin.'
              : 'Henüz bir web sitesi oluşturmadınız. Dakikalar içinde ilk sitenizi yayına hazırlayın!'}
          </p>
          <button
            onClick={() => setIsSectorSelectionOpen(true)}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-lg shadow-blue-500/25 transition inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            İlk Sitenizi Oluşturun
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredSites.map((site) => {
            const isYacht = site.industry === 'YACHT_RENTAL';
            const isCurrentActive = activeSite?.id === site.id;
            const isDropdownOpen = openDropdownSiteId === site.id;

            return (
              <div
                key={site.id}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden flex flex-col justify-between shadow-xs hover:shadow-md ${
                  isCurrentActive
                    ? 'border-blue-500 ring-2 ring-blue-500/20'
                    : 'border-slate-200/80 hover:border-slate-300'
                }`}
              >
                {/* Card Top / Header */}
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                          isYacht
                            ? 'bg-cyan-50 text-cyan-600'
                            : 'bg-emerald-50 text-emerald-600'
                        }`}
                      >
                        {isYacht ? <Ship className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        {isYacht ? 'Yat Kiralama' : 'Kurumsal'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span
                        className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                          site.status === 'PUBLISHED'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        {site.status === 'PUBLISHED' ? 'Yayında' : 'Taslak'}
                      </span>

                      {isCurrentActive && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          Aktif
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Site Title & Domain */}
                  <div>
                    <h3 className="text-base font-bold text-slate-900 truncate">{site.name}</h3>
                    <div className="mt-1 space-y-0.5">
                      <div className="text-xs font-mono text-slate-600 truncate flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{getSiteDisplayAddress(site)}</span>
                      </div>
                      {site.customDomain && (
                        <div className="text-xs font-mono text-emerald-700 truncate flex items-center gap-1.5 font-semibold">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="truncate">{site.customDomain}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Meta Stats: Son Güncelleme & Sayfa Sayısı */}
                  <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                    <div className="flex items-start gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-slate-400 block text-[10px] leading-tight">Son Güncelleme</span>
                        <span className="font-semibold text-slate-700 leading-tight">
                          {formatLastUpdate(site.updatedAt)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px] leading-tight">İçerik</span>
                      <span className="font-semibold text-slate-700 leading-tight">
                        {site.pages?.length || 0} Sayfa · {site.subscription?.planName || 'Standart'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Actions Footer: [Düzenle] [Önizle] [Yönet] [•••] */}
                <div className="p-3.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {/* [Düzenle] Primary Action - Direct to Visual Editor */}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSiteById(site.id);
                        onOpenEditor(site.id);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                      title="Görsel Editörde Düzenle"
                    >
                      <Paintbrush className="w-3.5 h-3.5" />
                      <span>Düzenle</span>
                    </button>

                    {/* [Önizle] */}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSiteById(site.id);
                        onOpenLiveSite(site);
                      }}
                      title="Canlı Önizleme"
                      className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                      <span className="hidden sm:inline">Önizle</span>
                    </button>

                    {/* [Yönet] */}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSiteById(site.id);
                        onSelectSite(site.id);
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-1"
                      title="Site Yönetim Merkezi"
                    >
                      <Settings className="w-3.5 h-3.5 text-slate-500" />
                      <span>Yönet</span>
                    </button>
                  </div>

                  {/* [•••] More Actions Dropdown */}
                  <div className="relative site-card-dropdown">
                    <button
                      type="button"
                      onClick={() => setOpenDropdownSiteId(isDropdownOpen ? null : site.id)}
                      title="Diğer Seçenekler"
                      className={`p-1.5 rounded-lg transition ${
                        isDropdownOpen
                          ? 'bg-slate-200 text-slate-900'
                          : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/60'
                      }`}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 bottom-full mb-1.5 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-30 text-xs animate-in fade-in">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSiteById(site.id);
                            setOpenDropdownSiteId(null);
                            setIsDuplicateModalOpen(true);
                          }}
                          className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 font-medium"
                        >
                          <Copy className="w-3.5 h-3.5 text-slate-400" />
                          <span>Siteyi Çoğalt (Klonla)</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setActiveSiteById(site.id);
                            setOpenDropdownSiteId(null);
                            onSelectSite(site.id);
                          }}
                          className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 font-medium"
                        >
                          <Link className="w-3.5 h-3.5 text-slate-400" />
                          <span>Alan Adı Bağla (DNS)</span>
                        </button>

                        <div className="border-t border-slate-100 my-1" />

                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdownSiteId(null);
                            setSiteToDelete(site);
                          }}
                          className="w-full text-left px-3.5 py-2 hover:bg-rose-50 text-rose-600 flex items-center gap-2 font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Website'i Tamamen Sil</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal (Requirement 18 & 19) */}
      {siteToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900">
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-slate-900">Website'i Tamamen Sil</h3>
              <p className="text-xs text-rose-600 font-semibold">
                Bu işlem geri alınamaz!
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">
                Bu işlem geri alınamaz. Sitenizin tüm sayfaları, içerikleri, görselleri ve ayarları kalıcı olarak silinecektir. Aboneliğiniz korunur ve 1 adet yeni website kurma hakkınız serbest kalır.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setSiteToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
              >
                {isDeleting ? 'Siliniyor...' : "Website'i Tamamen Sil"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Alert (Requirement 20) */}
      {deleteSuccessAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900 text-center">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-200">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-900">Website silindi</h3>
              <p className="text-xs text-slate-600">Website hakkınız yeniden kullanılabilir.</p>
            </div>
            <div className="pt-3 border-t border-slate-100 flex justify-center">
              <button
                type="button"
                onClick={() => setDeleteSuccessAlert(false)}
                className="px-6 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-sm"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Failure Alert (Requirement 21) */}
      {deleteErrorAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900 text-center">
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto border border-rose-200">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-900">İşlem Başarısız</h3>
              <p className="text-xs text-slate-600">Website silinemedi. Lütfen tekrar deneyin.</p>
            </div>
            <div className="pt-3 border-t border-slate-100 flex justify-center">
              <button
                type="button"
                onClick={() => setDeleteErrorAlert(false)}
                className="px-6 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-sm"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Has Active Website Modal (Requirement 13 & 14) */}
      {activeSiteExistsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto border border-amber-200">
              <AlertCircle className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-slate-900">Bu hesapta zaten aktif bir website bulunmaktadır.</h3>
              <p className="text-xs text-slate-600 leading-relaxed font-medium">
                1 hesapta yalnızca 1 aktif website barındırabilirsiniz. Mevcut sitenizi yönetebilir veya yeni bir site kurmak için mevcut sitenizi tamamen silebilirsiniz. İkinci bir siteyi aynı anda yayında tutmak istiyorsanız yeni bir Adiosoft hesabı oluşturmalısınız.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setActiveSiteExistsModalOpen(false)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
              >
                Kapat
              </button>
              <button
                type="button"
                onClick={handleCreateNewAccount}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Yeni Hesap Oluştur</span>
              </button>
              {activeNonDeletedSites.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSiteExistsModalOpen(false);
                      setSiteToDelete(activeNonDeletedSites[0]);
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold transition flex items-center justify-center gap-1.5 border border-rose-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Website'i Tamamen Sil</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSiteExistsModalOpen(false);
                      onOpenEditor(activeNonDeletedSites[0].id);
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/25"
                  >
                    <span>Mevcut Siteyi Yönet</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {isDuplicateModalOpen && (
        <SiteDuplicateModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
        />
      )}

      {/* Quota Exceeded Warning Modal */}
      {quotaExceededModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto border border-amber-200">
              <Layers className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-slate-900">Web Sitesi Oluşturma Hakkınız Doldu</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Mevcut abonelik paketiniz en fazla <span className="font-bold text-slate-900">{websiteLimit} web sitesi</span> barındırma hakkı içermektedir (Kullanılan: {sites.length} / {websiteLimit}).
              </p>
              <p className="text-xs text-slate-500">
                Yeni bir site oluşturmak için lütfen mevcut sitelerinizden birini silebilir veya ek paket talebinde bulunabilirsiniz.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setQuotaExceededModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
              >
                Kapat
              </button>
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => {
                    setQuotaExceededModalOpen(false);
                    onNavigate('billing');
                  }}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-md shadow-blue-500/25"
                >
                  <span>Aboneliği Görüntüle</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sektör Seçim & Kota Karşılama Modalı */}
      <SectorSelectionModal
        isOpen={isSectorSelectionOpen}
        onClose={() => setIsSectorSelectionOpen(false)}
        onSelectSector={(sector, mode) => {
          setIsSectorSelectionOpen(false);
          if (mode === 'ai' && onOpenAiWizard) {
            onOpenAiWizard(sector);
          } else {
            onOpenWizard(sector);
          }
        }}
        onOpenBilling={() => {
          setIsSectorSelectionOpen(false);
          if (onNavigate) {
            onNavigate('billing');
          }
        }}
      />

      {/* HTML / CSS / JS Export Modal */}
      {exportModalSite && (
        <WebsiteExportModal
          site={exportModalSite}
          isOpen={!!exportModalSite}
          onClose={() => setExportModalSite(null)}
        />
      )}
    </div>
  );
};

